import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircleNotch, WarningCircle } from 'phosphor-react';
import mainLogo from '../../../assets/logo-header.png';
import { GuestWaiverDisplay, GuestMemberResult } from '@/types/guestWaiver';
import { guestWaiverService } from '@/services/guestWaiverService';
import styles from './GuestWaiver.module.css';

type PageState = 'loading' | 'error' | 'form' | 'submitting' | 'success';

export default function GuestWaiverPage() {
  const { lId = '' } = useParams<{ lId: string }>();
  const { t } = useTranslation();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [waiver, setWaiver] = useState<GuestWaiverDisplay | null>(null);
  const [locationName, setLocationName] = useState('');

  // Form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');

  // Field errors
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [emailError, setEmailError] = useState('');

  // Member typeahead
  const [memberQuery, setMemberQuery] = useState('');
  const [memberResults, setMemberResults] = useState<GuestMemberResult[]>([]);
  const [memberId, setMemberId] = useState<string | undefined>(undefined);
  const [selectedMemberName, setSelectedMemberName] = useState<
    string | undefined
  >(undefined);
  const [memberSearching, setMemberSearching] = useState(false);
  const [memberSearchError, setMemberSearchError] = useState(false);
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);

  // CTA
  const [agreed, setAgreed] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Ref to track debounce timer
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref to track if member was selected (to avoid re-clearing memberId on same query)
  const memberSelectedRef = useRef(false);

  // Mount effect: load waiver
  useEffect(() => {
    if (!lId) {
      setPageState('error');
      return;
    }
    guestWaiverService
      .getWaiver(lId)
      .then((data) => {
        setWaiver(data);
        setLocationName(data.locationName);
        setPageState('form');
      })
      .catch(() => {
        setPageState('error');
      });
  }, [lId]);

  // Member typeahead effect
  useEffect(() => {
    if (memberSelectedRef.current) {
      // If member was just selected, skip this effect cycle
      memberSelectedRef.current = false;
      return;
    }

    // Clear memberId if user typed after a previous selection
    if (memberId !== undefined) {
      setMemberId(undefined);
      setSelectedMemberName(undefined);
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (memberQuery.length < 2) {
      setMemberResults([]);
      setShowMemberDropdown(false);
      setMemberSearchError(false);
      return;
    }

    setMemberSearching(true);
    debounceTimer.current = setTimeout(async () => {
      try {
        const results = await guestWaiverService.searchMembers(
          lId,
          memberQuery,
        );
        setMemberResults(results);
        setMemberSearchError(false);
        setShowMemberDropdown(true);
      } catch {
        setMemberSearchError(true);
        setMemberResults([]);
        setShowMemberDropdown(true);
      } finally {
        setMemberSearching(false);
      }
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberQuery]);

  const handleMemberSelect = useCallback((result: GuestMemberResult) => {
    memberSelectedRef.current = true;
    setMemberId(result.memberId);
    setSelectedMemberName(`${result.firstName} ${result.lastName}`);
    setMemberQuery('');
    setMemberResults([]);
    setShowMemberDropdown(false);
    setMemberSearchError(false);
  }, []);

  const clearMember = useCallback(() => {
    setMemberId(undefined);
    setSelectedMemberName(undefined);
    setMemberQuery('');
    setMemberResults([]);
    setShowMemberDropdown(false);
  }, []);

  const validateEmail = (value: string): boolean => {
    if (!value.trim()) return true; // empty is valid (optional)
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  };

  const handleSubmit = useCallback(async () => {
    let valid = true;

    if (!firstName.trim()) {
      setFirstNameError(t('guestWaiver.fieldRequired.firstName'));
      valid = false;
    } else {
      setFirstNameError('');
    }

    if (!lastName.trim()) {
      setLastNameError(t('guestWaiver.fieldRequired.lastName'));
      valid = false;
    } else {
      setLastNameError('');
    }

    if (email && !validateEmail(email)) {
      setEmailError(t('guestWaiver.fieldInvalid.email'));
      valid = false;
    } else {
      setEmailError('');
    }

    if (!valid) return;

    setPageState('submitting');
    setSubmitError('');

    try {
      await guestWaiverService.submitWaiver({
        lId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim() || undefined,
        memberId,
        guestSource: 'link',
      });
      setPageState('success');
    } catch {
      setPageState('form');
      setSubmitError(t('guestWaiver.submissionError'));
    }
  }, [firstName, lastName, email, memberId, lId, t]);

  const isSubmitEnabled =
    firstName.trim().length >= 1 &&
    lastName.trim().length >= 1 &&
    agreed === true;

  const isSubmitting = pageState === 'submitting';

  // ---- State 1: Loading ----
  if (pageState === 'loading') {
    return (
      <div className={styles.guestWaiverPage}>
        <div className={styles.pageContent}>
          <img src={mainLogo} alt="Tee Box" className={styles.logo} />
          <div className={styles.formSection}>
            <div
              className={`${styles.skeletonBlock} ${styles.skeletonInput}`}
            />
            <div
              className={`${styles.skeletonBlock} ${styles.skeletonInput}`}
            />
          </div>
          <div className={styles.waiverCard}>
            <div className={styles.scrollArea}>
              <div
                className={`${styles.skeletonBlock} ${styles.skeletonTitle}`}
              />
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`${styles.skeletonBlock} ${styles.skeletonLine}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- State 2: Error ----
  if (pageState === 'error') {
    return (
      <div className={styles.guestWaiverPage}>
        <div className={styles.pageContent}>
          <img src={mainLogo} alt="Tee Box" className={styles.logo} />
          <div className={styles.errorCard}>
            <WarningCircle size={40} className={styles.errorIcon} />
            <p className={styles.errorHeading}>{t('guestWaiver.errorHeading')}</p>
            <p className={styles.errorBody}>{t('guestWaiver.errorBody')}</p>
          </div>
        </div>
      </div>
    );
  }

  // ---- State 5: Success ----
  if (pageState === 'success') {
    return (
      <div className={styles.guestWaiverPage}>
        <div className={styles.pageContent}>
          <img src={mainLogo} alt="Tee Box" className={styles.logo} />
          <div className={styles.successCard}>
            <span className={styles.successIcon}>✓</span>
            <p className={styles.successHeading}>
              {t('guestWaiver.successHeading', { firstName: firstName.trim() })}
            </p>
            <p className={styles.successBody}>{t('guestWaiver.successBody')}</p>
            {email.trim() && (
              <p className={styles.successBody}>
                {t('guestWaiver.successEmailConfirmation')}
              </p>
            )}
            <p className={styles.successMeta}>{locationName}</p>
          </div>
        </div>
      </div>
    );
  }

  // ---- States 3 & 4: Form / Submitting ----
  const waiverMeta = (() => {
    if (!waiver) return '';
    const version = `Version ${waiver.version}`;
    if (!waiver.publishedAt) return version;
    const dateStr = new Date(waiver.publishedAt).toLocaleDateString('en-US', {
      dateStyle: 'long',
    } as Intl.DateTimeFormatOptions);
    return `${version} · Effective ${dateStr}`;
  })();

  return (
    <div className={styles.guestWaiverPage}>
      <div className={styles.pageContent}>
      <img src={mainLogo} alt="Tee Box" className={styles.logo} />
      <p className={styles.pageTitle}>{t('guestWaiver.pageTitle')}</p>
      <p className={styles.pageSubtitle}>{t('guestWaiver.pageSubtitle')}</p>

      {/* Form fields */}
      <div className={styles.formSection}>
        {/* First Name */}
        <div className={styles.fieldWrapper}>
          <label className={styles.fieldLabel} htmlFor="gw-firstName">
            {t('guestWaiver.firstNameLabel')}
          </label>
          <input
            id="gw-firstName"
            type="text"
            autoComplete="given-name"
            placeholder={t('guestWaiver.firstNamePlaceholder')}
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              if (firstNameError && e.target.value.trim()) {
                setFirstNameError('');
              }
            }}
            onBlur={() => {
              if (!firstName.trim()) {
                setFirstNameError(t('guestWaiver.fieldRequired.firstName'));
              }
            }}
            disabled={isSubmitting}
            className={[
              styles.fieldInput,
              isSubmitting ? styles.fieldInputDisabled : '',
            ]
              .filter(Boolean)
              .join(' ')}
          />
          {firstNameError && (
            <p className={styles.fieldError}>{firstNameError}</p>
          )}
        </div>

        {/* Last Name */}
        <div className={styles.fieldWrapper}>
          <label className={styles.fieldLabel} htmlFor="gw-lastName">
            {t('guestWaiver.lastNameLabel')}
          </label>
          <input
            id="gw-lastName"
            type="text"
            autoComplete="family-name"
            placeholder={t('guestWaiver.lastNamePlaceholder')}
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              if (lastNameError && e.target.value.trim()) {
                setLastNameError('');
              }
            }}
            onBlur={() => {
              if (!lastName.trim()) {
                setLastNameError(t('guestWaiver.fieldRequired.lastName'));
              }
            }}
            disabled={isSubmitting}
            className={[
              styles.fieldInput,
              isSubmitting ? styles.fieldInputDisabled : '',
            ]
              .filter(Boolean)
              .join(' ')}
          />
          {lastNameError && (
            <p className={styles.fieldError}>{lastNameError}</p>
          )}
        </div>

        {/* Email */}
        <div className={styles.fieldWrapper}>
          <label className={styles.fieldLabel} htmlFor="gw-email">
            {t('guestWaiver.emailLabel')}
            <span className={styles.fieldHint}>{t('guestWaiver.emailHint')}</span>
          </label>
          <input
            id="gw-email"
            type="email"
            autoComplete="email"
            placeholder={t('guestWaiver.emailPlaceholder')}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError('');
            }}
            onBlur={() => {
              if (email && !validateEmail(email)) {
                setEmailError(t('guestWaiver.fieldInvalid.email'));
              } else {
                setEmailError('');
              }
            }}
            disabled={isSubmitting}
            className={[
              styles.fieldInput,
              isSubmitting ? styles.fieldInputDisabled : '',
            ]
              .filter(Boolean)
              .join(' ')}
          />
          {emailError && <p className={styles.fieldError}>{emailError}</p>}
        </div>

        {/* Member Name typeahead */}
        <div className={styles.fieldWrapper}>
          <label className={styles.fieldLabel} htmlFor="gw-member">
            {t('guestWaiver.memberLabel')}
            <span className={styles.fieldHint}>{t('guestWaiver.memberHint')}</span>
          </label>
          <div className={styles.typeaheadWrapper}>
            <div className={styles.typeaheadInputWrapper}>
              <input
                id="gw-member"
                type="text"
                placeholder={t('guestWaiver.memberPlaceholder')}
                value={memberQuery}
                onChange={(e) => setMemberQuery(e.target.value)}
                disabled={isSubmitting}
                className={[
                  styles.fieldInput,
                  isSubmitting ? styles.fieldInputDisabled : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                autoComplete="off"
              />
              {memberSearching && (
                <span className={styles.typeaheadSpinner}>
                  <CircleNotch size={14} className={styles.spinning} />
                </span>
              )}
            </div>

            {showMemberDropdown && (
              <div className={styles.typeaheadDropdown}>
                {memberSearchError ? (
                  <div className={styles.typeaheadEmpty}>
                    {t('guestWaiver.memberSearchError')}
                  </div>
                ) : memberResults.length === 0 ? (
                  <div className={styles.typeaheadEmpty}>
                    {t('guestWaiver.memberNoResults')}
                  </div>
                ) : (
                  memberResults.map((result) => (
                    <div
                      key={result.memberId}
                      className={styles.typeaheadRow}
                      onClick={() => handleMemberSelect(result)}
                    >
                      <div className={styles.typeaheadRowName}>
                        {result.firstName} {result.lastName}
                      </div>
                      <div className={styles.typeaheadRowMeta}>
                        {result.maskedEmail}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {selectedMemberName && (
            <div className={styles.sponsoredByChip}>
              <span>{t('guestWaiver.memberChip', { name: selectedMemberName })}</span>
              <button
                type="button"
                className={styles.sponsoredByChipClear}
                onClick={clearMember}
                aria-label="Clear member selection"
              >
                ×
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Waiver card */}
      {waiver && (
        <div className={styles.waiverCard}>
          <div className={styles.scrollArea}>
            <p className={styles.waiverTitle}>{waiver.title}</p>
            <p className={styles.waiverMeta}>{waiverMeta}</p>
            <div
              className={styles.waiverContent}
              dangerouslySetInnerHTML={{ __html: waiver.body }}
            />
          </div>
        </div>
      )}

      {/* CTA strip */}
      <div className={styles.ctaStrip}>
        <label className={styles.agreeRow}>
          <input
            type="checkbox"
            className={styles.agreeCheckbox}
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={isSubmitting}
          />
          <span className={styles.agreeLabel}>
            {t('guestWaiver.agreeCheckbox')}
          </span>
        </label>

        {submitError && (
          <p className={styles.fieldError}>{submitError}</p>
        )}

        <button
          type="button"
          className={[
            styles.submitButton,
            !isSubmitEnabled || isSubmitting ? styles.submitButtonDisabled : '',
          ]
            .filter(Boolean)
            .join(' ')}
          disabled={!isSubmitEnabled || isSubmitting}
          onClick={handleSubmit}
        >
          {isSubmitting ? (
            <>
              <CircleNotch size={20} className={styles.spinning} />
              {t('guestWaiver.submittingButton')}
            </>
          ) : (
            t('guestWaiver.submitButton')
          )}
        </button>
      </div>
      </div>
    </div>
  );
}
